import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Tooltip,
  Badge,
  List,
  ListItem,
  ListItemText,
  Rating,
  Avatar,
  InputAdornment,
  alpha,
  Checkbox,
  FormControlLabel,
  Switch,
  Menu,
  Popover,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PhoneIcon from '@mui/icons-material/Phone';
import MoodIcon from '@mui/icons-material/Mood';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CallEndIcon from '@mui/icons-material/CallEnd';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LinearProgress from '@mui/material/LinearProgress';
import { format } from 'date-fns';
import { formatDistanceToNow } from 'date-fns';
import { ScheduledCall, OutboundCallConfig } from '../types';
import { callSchedulingApi } from '../services/api';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PercentIcon from '@mui/icons-material/Percent';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SmsIcon from '@mui/icons-material/Sms';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AgentLabel from './AgentLabel';
import AgentSelect from './AgentSelect';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';
import ReplayIcon from '@mui/icons-material/Replay';

import { campaignApi } from '../services/campaignApi';

// Add interface for Campaign
interface Campaign {
  id: string;
  name: string;
}

// Add interface for metrics data
interface CallMetrics {
  behavior_score?: number;
  behavior_score_reasoning?: string;
  call_duration?: string;
  call_sid?: string;
  call_termination?: string;
  call_time?: string;
  called_number?: string;
  caller_number?: string;
  caller_satisfaction?: number;
  caller_satisfaction_reasoning?: string;
  horrible_call?: boolean;
  sentiment_analysis?: string;
  transcript?: Array<{
    content: string;
    role: string;
  }>;
  [key: string]: any;
}

// Add interface for sorting
type SortField = 'schedule_dt' | 'created_dt' | 'from_number' | 'to_number' | 'status' | 'agent_id';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  order: SortOrder;
}

// Add interface for tool usage
interface ToolUsage {
  time_to_perform: number;
  tool_call_time: string;
  tool_input: Record<string, any>;
  tool_name: string;
  tool_result: string;
  tool_return_time: string;
}

interface Filters {
  status: string[];
  statusMode: 'AND' | 'OR';
  from_dt: Date | null;
  to_dt: Date | null;
  agent_id: string;
  from_number: string;
  to_number: string;
  campaign_id: string;
}

const defaultFilters: Filters = {
  status: [],
  statusMode: 'OR',
  from_dt: null,
  to_dt: null,
  agent_id: '',
  from_number: '',
  to_number: '',
  campaign_id: ''
};

const ScheduledCallsList: React.FC = () => {
  const [calls, setCalls] = useState<ScheduledCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Filters>(() => {
    // Initialize filters from URL parameters
    const params = new URLSearchParams(window.location.search);
    
    // Handle status array properly - split any comma-separated values
    const statusParam = params.getAll('status');
    const statuses = statusParam.length > 0 
      ? statusParam.flatMap(s => s.split(',').filter(Boolean))
      : [];

    return {
      status: statuses,
      statusMode: params.get('statusMode') === 'AND' ? 'AND' : 'OR',
      from_dt: params.get('from_dt') ? new Date(String(params.get('from_dt'))) : null,
      to_dt: params.get('to_dt') ? new Date(String(params.get('to_dt'))) : null,
      agent_id: params.get('agent_id') || '',
      from_number: params.get('from_number') || '',
                to_number: params.get('to_number') || '',
                campaign_id: params.get('campaign_id') || ''
              };  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [callToDelete, setCallToDelete] = useState<string | null>(null);
  const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [selectedCallMetrics, setSelectedCallMetrics] = useState<CallMetrics | null>(null);
  const [selectedCallDetails, setSelectedCallDetails] = useState<ScheduledCall | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ 
    field: 'schedule_dt', 
    order: 'desc' 
  });
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  useEffect(() => {
    fetchCalls();
    // Channels are prefetched when navigating to list page, no need to fetch again
    // Fetch campaigns
    campaignApi.getCampaigns()
      .then(setCampaigns)
      .catch(error => console.error('Error loading campaigns:', error));
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Clear existing status parameters
    params.delete('status');
    
    // Add each status individually
    filters.status.forEach(status => {
      params.append('status', status);
    });
    
    // Update other filter parameters
    if (filters.statusMode === 'AND') {
      params.set('statusMode', 'AND');
    } else {
      params.delete('statusMode'); // Default is OR
    }
    
    if (filters.from_dt) {
      params.set('from_dt', filters.from_dt.toISOString());
    } else {
      params.delete('from_dt');
    }
    
    if (filters.to_dt) {
      params.set('to_dt', filters.to_dt.toISOString());
    } else {
      params.delete('to_dt');
    }
    
    if (filters.agent_id) {
      params.set('agent_id', filters.agent_id.toString());
    } else {
      params.delete('agent_id');
    }
    
    if (filters.from_number) {
      params.set('from_number', filters.from_number);
    } else {
      params.delete('from_number');
    }
    
    if (filters.to_number) {
      params.set('to_number', filters.to_number);
    } else {
      params.delete('to_number');
    }

    if (filters.campaign_id) {
      params.set('campaign_id', filters.campaign_id);
    } else {
      params.delete('campaign_id');
    }

    // Update URL without reloading
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [filters]);

  // Handle URL parameter changes
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      
      // Handle status array properly - split any comma-separated values
      const statusParam = params.getAll('status');
      const statuses = statusParam.length > 0 
        ? statusParam.flatMap(s => s.split(',').filter(Boolean))
        : [];
      
      // Update filters from URL parameters
      setFilters({
        status: statuses,
        statusMode: params.get('statusMode') === 'AND' ? 'AND' : 'OR',
        from_dt: params.get('from_dt') ? new Date(String(params.get('from_dt'))) : null,
        to_dt: params.get('to_dt') ? new Date(String(params.get('to_dt'))) : null,
        agent_id: params.get('agent_id') || '',
        from_number: params.get('from_number') || '',
        to_number: params.get('to_number') || '',
        campaign_id: params.get('campaign_id') || ''
      });
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  // Initial load of selected call from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const callId = params.get('call');
    if (callId) {
      fetchCallDetails(callId);
    }
    
    // Also trigger initial filter fetch
    fetchCalls();
  }, []);

  const fetchCallDetails = async (callId: string) => {
    try {
      // Use the existing getScheduledCalls API to get the call details
      const response = await callSchedulingApi.getScheduledCalls();
      if (Array.isArray(response)) {
        const call = response.find((c: ScheduledCall) => c.id?.toString() === callId);
        if (call) {
          setSelectedCallDetails(call);
        }
      }
    } catch (error) {
      console.error('Error fetching call details:', error);
    }
  };

  // Load calls on component mount and when filters change
  useEffect(() => {
    fetchCalls();
  }, []);

  // Add effect to log calls when they change
  useEffect(() => {
    // console.log('Calls state updated:', calls);
  }, [calls]);

  const handleFilterChange = (name: keyof Filters, value: any) => {
    // Handle null/undefined values
    const filterValue = value === null || value === undefined ? '' : value;
    
    // Create the new filters object
    const newFilters = { ...filters };

    // Special handling for agent_id
    if (name === 'agent_id' && typeof filterValue === 'number') {
      newFilters[name] = filterValue.toString();
    }
    // Special handling for dates
    else if ((name === 'from_dt' || name === 'to_dt') && filterValue !== '') {
      newFilters[name] = filterValue;
    }
    // Special handling for status array
    else if (name === 'status') {
      newFilters[name] = Array.isArray(filterValue) ? filterValue : [filterValue];
    }
    // Handle all other cases
    else {
      newFilters[name] = filterValue;
    }

    // Update state
    setFilters(newFilters);
    
    // Use the new filters (including the new mode if that's what changed)
    // to prepare the filter parameters
    prepareAndFetchWithFilters(newFilters);
  };

  const prepareAndFetchWithFilters = (filters: Filters) => {
    // Prepare filter parameters with the new values
    const filterParams: any = {};
    
    // Special handling for status based on mode and selected statuses
    if (filters.status.length > 0) {
      // Get the selected basic statuses (excluding metadata statuses like reminder, flagged, etc)
      const selectedBasicStatuses = filters.status.filter(s => 
        ['pending', 'placed', 'failed'].includes(s)
      );

      if (selectedBasicStatuses.length > 0) {
        // In OR mode, send all statuses to be handled by separate requests
        if (filters.statusMode === 'OR') {
          filterParams.status = selectedBasicStatuses.join(',');
        } else {
          // In AND mode, we need to determine the most specific status
          if (selectedBasicStatuses.includes('failed')) {
            filterParams.status = 'failed';
          } else if (selectedBasicStatuses.includes('placed')) {
            filterParams.status = 'placed';
          } else if (selectedBasicStatuses.includes('pending')) {
            filterParams.status = 'pending';
          }
        }
      }

      // Handle metadata filters
      const metadataFilters = filters.status.filter(s => 
        ['reminder', 'flagged', 'has_tasks', 'has_messages', 'has_actions'].includes(s)
      );

      if (metadataFilters.length > 0) {
        filterParams.metadata_filters = metadataFilters;
      }
    }

    if (filters.agent_id) filterParams.agent_id = filters.agent_id;
    if (filters.from_dt) filterParams.from_dt = format(filters.from_dt, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    if (filters.to_dt) filterParams.to_dt = format(filters.to_dt, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    if (filters.from_number) filterParams.from_number = filters.from_number;
    if (filters.to_number) filterParams.to_number = filters.to_number;
    if (filters.campaign_id) filterParams.campaign_id = filters.campaign_id;

    // Pass the current mode to fetchCallsWithParams
    fetchCallsWithParams(filterParams, filters.statusMode);
  };

  const fetchCallsWithParams = async (filterParams: any, statusMode: 'AND' | 'OR') => {
    try {
      setLoading(true);
      setError(null);

      let allCalls: ScheduledCall[] = [];
      
      // In OR mode with multiple statuses, we need to make separate requests
      if (statusMode === 'OR' && filterParams.status) {
        const statuses = filterParams.status.split(',');
        const metadataFilters = filterParams.metadata_filters || [];
        
        // Remove status and metadata_filters from base params
        const baseParams = { ...filterParams };
        delete baseParams.status;
        delete baseParams.metadata_filters;

        // Make separate requests for each status
        const statusRequests = statuses.map((status: string) => 
          callSchedulingApi.getScheduledCalls({ ...baseParams, status })
        );

        // Wait for all status requests
        const statusResults = await Promise.all(statusRequests);
        
        // Combine and deduplicate results
        const seenIds = new Set<string>();
        for (const result of statusResults) {
          if (result?.scheduled_calls) {
            const processedCalls = processCallsData(result.scheduled_calls);
            processedCalls.forEach(call => {
              if (!seenIds.has(call.id!)) {
                seenIds.add(call.id!);
                allCalls.push(call);
              }
            });
          }
        }

        // If we have metadata filters, we need to make separate requests for those too
        if (metadataFilters.length > 0) {
          // Make a request without any status filter
          const metadataResult = await callSchedulingApi.getScheduledCalls(baseParams);
          if (metadataResult?.scheduled_calls) {
            const processedCalls = processCallsData(metadataResult.scheduled_calls);
            
            // Filter by metadata conditions
            const metadataFilteredCalls = processedCalls.filter(call => {
              return metadataFilters.some((filter: string) => {
                switch (filter) {
                  case 'reminder':
                    const config = typeof call.outbound_call_config === 'string' 
                      ? JSON.parse(call.outbound_call_config) 
                      : call.outbound_call_config;
                    return config.is_reminder;
                  case 'flagged':
                    return call.metrics?.horrible_call === true;
                  case 'has_tasks':
                    return (call.metrics?.auto_tasks?.extracted_tasks?.length ?? 0) > 0;
                  case 'has_messages':
                    return (call.metrics?.auto_text_messages?.extracted_messages?.length ?? 0) > 0;
                  case 'has_actions':
                    return (call.metrics?.tools_used?.length ?? 0) > 0;
                  default:
                    return false;
                }
              });
            });

            // Add unique metadata filtered calls
            metadataFilteredCalls.forEach(call => {
              if (!seenIds.has(call.id!)) {
                seenIds.add(call.id!);
                allCalls.push(call);
              }
            });
          }
        }

        setCalls(allCalls);
      }
      // In AND mode or single status, we can use a single request
      else {
        const response = await callSchedulingApi.getScheduledCalls(filterParams);
        if (response && response.scheduled_calls) {
          let processedCalls = processCallsData(response.scheduled_calls);

          // Apply metadata filters if any exist
          if (filterParams.metadata_filters?.length > 0) {
            processedCalls = processedCalls.filter(call => {
              return filterParams.metadata_filters.every((filter: string) => {
                switch (filter) {
                  case 'reminder':
                    const config = typeof call.outbound_call_config === 'string' 
                      ? JSON.parse(call.outbound_call_config) 
                      : call.outbound_call_config;
                    return config.is_reminder;
                  case 'flagged':
                    return call.metrics?.horrible_call === true;
                  case 'has_tasks':
                    return (call.metrics?.auto_tasks?.extracted_tasks?.length ?? 0) > 0;
                  case 'has_messages':
                    return (call.metrics?.auto_text_messages?.extracted_messages?.length ?? 0) > 0;
                  case 'has_actions':
                    return (call.metrics?.tools_used?.length ?? 0) > 0;
                  default:
                    return false;
                }
              });
            });
          }

          setCalls(processedCalls);
        } else {
          console.warn('No scheduled_calls in response:', response);
          setCalls([]);
        }
      }
    } catch (err: any) {
      console.error('Error fetching calls:', err);
      setError(err.message || 'Failed to load scheduled calls');
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  // Update fetchCalls to use the new helper
  const fetchCalls = async () => {
    prepareAndFetchWithFilters(filters);
  };

  // Process the calls data to handle string JSON fields
  const processCallsData = (calls: ScheduledCall[] | undefined | null): ScheduledCall[] => {
    if (!calls || !Array.isArray(calls)) {
      return [];
    }
    return calls.map(call => {
      // console.log('Processing call:', call);
      const processedCall = { ...call };
      
      // Parse outbound_call_config if it's a string
      if (typeof processedCall.outbound_call_config === 'string') {
        try {
          processedCall.outbound_call_config = JSON.parse(processedCall.outbound_call_config) as OutboundCallConfig;
        } catch (err) {
          console.error('Error parsing outbound_call_config:', err);
          processedCall.outbound_call_config = {} as OutboundCallConfig;
        }
      }
      
      // Parse metrics if it's a string
      if (processedCall.metrics && typeof processedCall.metrics === 'string') {
        try {
          processedCall.metrics = JSON.parse(processedCall.metrics);
        } catch (err) {
          console.error('Error parsing metrics:', err);
          processedCall.metrics = null;
        }
      }

      // Log metrics data for debugging
      // if (processedCall.metrics?.auto_tasks || processedCall.metrics?.auto_text_messages) {
      //   console.log('Found metrics with automation data:', {
      //     auto_tasks: processedCall.metrics.auto_tasks,
      //     auto_text_messages: processedCall.metrics.auto_text_messages
      //   });
      // }
      
      return processedCall;
    });
  };

  // Add sorting function
  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }> | any) => {
    const value = event.target.value as string;
    const [field, order] = value.split(':') as [SortField, SortOrder];
    setSortConfig({ field, order });
  };

  // Function to sort calls based on current sort config
  const getSortedCalls = () => {
    const sortableCalls = Array.isArray(calls) ? [...calls] : [];
    
    return sortableCalls.sort((a, b) => {
      switch (sortConfig.field) {
        case 'schedule_dt':
          // Handle null values
          if (!a.schedule_dt && !b.schedule_dt) return 0;
          if (!a.schedule_dt) return sortConfig.order === 'asc' ? -1 : 1;
          if (!b.schedule_dt) return sortConfig.order === 'asc' ? 1 : -1;
          
          // Compare dates
          return sortConfig.order === 'asc' 
            ? new Date(a.schedule_dt).getTime() - new Date(b.schedule_dt).getTime()
            : new Date(b.schedule_dt).getTime() - new Date(a.schedule_dt).getTime();
            
        case 'created_dt':
          // Handle null values
          if (!a.created_dt && !b.created_dt) return 0;
          if (!a.created_dt) return sortConfig.order === 'asc' ? -1 : 1;
          if (!b.created_dt) return sortConfig.order === 'asc' ? 1 : -1;
          
          // Compare dates
          return sortConfig.order === 'asc' 
            ? new Date(a.created_dt).getTime() - new Date(b.created_dt).getTime()
            : new Date(b.created_dt).getTime() - new Date(a.created_dt).getTime();
            
        case 'from_number':
          return sortConfig.order === 'asc'
            ? a.from_number.localeCompare(b.from_number)
            : b.from_number.localeCompare(a.from_number);
            
        case 'to_number':
          return sortConfig.order === 'asc'
            ? a.to_number.localeCompare(b.to_number)
            : b.to_number.localeCompare(a.to_number);
            
        case 'agent_id':
          // Compare agent IDs as numbers when possible
          const agentIdA = typeof getAgentId(a) === 'number' ? getAgentId(a) : String(getAgentId(a));
          const agentIdB = typeof getAgentId(b) === 'number' ? getAgentId(b) : String(getAgentId(b));
          
          if (typeof agentIdA === 'number' && typeof agentIdB === 'number') {
            return sortConfig.order === 'asc'
              ? agentIdA - agentIdB
              : agentIdB - agentIdA;
          } else {
            // Fall back to string comparison if not numbers
            return sortConfig.order === 'asc'
              ? String(agentIdA).localeCompare(String(agentIdB))
              : String(agentIdB).localeCompare(String(agentIdA));
          }
            
        case 'status':
          // Create status values for sorting
          const getStatusValue = (call: ScheduledCall) => {
            if (call.call_placed) return 3;
            if (call.call_attempted && !call.call_placed) return 2;
            if (call.call_attempted) return 1;
            return 0;
          };
          
          const statusA = getStatusValue(a);
          const statusB = getStatusValue(b);
          
          return sortConfig.order === 'asc'
            ? statusA - statusB
            : statusB - statusA;
            
        default:
          return 0;
      }
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const applyFilters = () => {
    setPage(0);
    fetchCalls();
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(0);
    fetchCalls();
  };

  const openDeleteDialog = (callId: string) => {
    setCallToDelete(callId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setCallToDelete(null);
    setDeleteDialogOpen(false);
  };

  const confirmDelete = async () => {
    if (!callToDelete) return;
    
    try {
      setLoading(true);
      await callSchedulingApi.cancelScheduledCall(callToDelete);
      setSuccess('Call cancelled successfully');
      fetchCalls();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel call');
    } finally {
      setLoading(false);
      closeDeleteDialog();
    }
  };

  const toggleCallSelection = (callId: string) => {
    setSelectedCalls(prev => {
      if (prev.includes(callId)) {
        return prev.filter(id => id !== callId);
      } else {
        return [...prev, callId];
      }
    });
  };

  const openBatchDeleteDialog = () => {
    if (selectedCalls.length === 0) return;
    setBatchDeleteDialogOpen(true);
  };

  const closeBatchDeleteDialog = () => {
    setBatchDeleteDialogOpen(false);
  };

  const confirmBatchDelete = async () => {
    try {
      setLoading(true);
      await callSchedulingApi.batchCancelScheduledCalls(selectedCalls);
      setSuccess(`${selectedCalls.length} calls cancelled successfully`);
      setSelectedCalls([]);
      fetchCalls();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel calls');
    } finally {
      setLoading(false);
      closeBatchDeleteDialog();
    }
  };

  const getStatusChip = (call: ScheduledCall) => {
    if (call.call_placed) {
      return <Chip label="Placed" color="success" size="small" />;
    } else if (call.call_attempted) {
      return <Chip label="Failed" color="error" size="small" />;
    } else {
      return <Chip label="Pending" color="primary" size="small" />;
    }
  };

  // Safe accessor for agent_id
  const getAgentId = (call: ScheduledCall) => {
    try {
      // If outbound_call_config is a string, try to parse it
      let config = call.outbound_call_config;
      
      // Parse config if it's a string
      if (typeof config === 'string') {
        try {
          config = JSON.parse(config) as OutboundCallConfig;
        } catch (err) {
          console.error('Error parsing outbound_call_config in getAgentId:', err);
          return null;
        }
      }
      
      // Use type assertion to tell TypeScript this is an OutboundCallConfig
      const typedConfig = config as OutboundCallConfig;
      return typedConfig.agent_id;
    } catch (err) {
      console.error('Error in getAgentId:', err);
      return null;
    }
  };

  // Added function to open the metrics dialog
  const openMetricsDialog = (call: ScheduledCall) => {
    if (call.metrics) {
      setSelectedCallMetrics(call.metrics as unknown as CallMetrics);
      setSelectedCallDetails(call);
      setMetricsDialogOpen(true);
    }
  };

  // Added function to close the metrics dialog
  const closeMetricsDialog = () => {
    setMetricsDialogOpen(false);
    setSelectedCallMetrics(null);
    setSelectedCallDetails(null);
  };

  // Added function to check if a call has metrics
  const hasMetrics = (call: ScheduledCall) => {
    return call.metrics && Object.keys(call.metrics).length > 0;
  };

  // Function to render transcript messages
  const renderTranscript = (transcript: Array<{content: string, role: string}> | undefined) => {
    if (!transcript || transcript.length === 0) {
      return <Typography variant="body2">No transcript available</Typography>;
    }

    return (
      <List sx={{ bgcolor: 'background.paper', maxHeight: 300, overflow: 'auto' }}>
        {(transcript || []).map((message, index) => (
          <ListItem key={index} alignItems="flex-start" sx={{
            borderLeft: message.role === 'assistant' ? '4px solid #3f51b5' : '4px solid #f50057',
            mb: 1,
            backgroundColor: message.role === 'assistant' ? 'rgba(63, 81, 181, 0.08)' : 'rgba(245, 0, 87, 0.08)'
          }}>
            <ListItemText
              primary={
                <Typography
                  variant="subtitle2"
                  color={message.role === 'assistant' ? 'primary' : 'text.primary'}
                >
                  {message.role === 'assistant' ? 'AI Assistant' : 'Customer'}
                </Typography>
              }
              secondary={message.content}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  // Function to render score with emotion icon
  const renderScore = (score: number | undefined, max: number = 5) => {
    if (score === undefined) return 'N/A';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Rating value={score} readOnly max={max} />
        <Box sx={{ ml: 1 }}>
          {score <= max/3 && <SentimentVeryDissatisfiedIcon color="error" />}
          {score > max/3 && score <= 2*max/3 && <SentimentSatisfiedIcon color="warning" />}
          {score > 2*max/3 && <MoodIcon color="success" />}
        </Box>
      </Box>
    );
  };

  // Format agent_id for display
  const formatAgentId = (agentId: string | number) => {
    if (typeof agentId === 'number' || (typeof agentId === 'string' && /^\d+$/.test(agentId))) {
      // If it's a numeric string or number, ensure it's displayed as a number
      return typeof agentId === 'string' ? parseInt(agentId, 10) : agentId;
    }
    return agentId;
  };

  const getMetricDisplay = (metric: any, key: string) => {
    // Handle different metric types
    if (typeof metric === 'number') {
      if (key.toLowerCase().includes('score')) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating value={metric} readOnly precision={0.5} />
            <Typography variant="body2" color="text.secondary">
              ({metric.toFixed(1)})
            </Typography>
          </Box>
        );
      }
      return metric.toFixed(2);
    }
    if (typeof metric === 'boolean') {
      return metric ? 'Yes' : 'No';
    }
    if (typeof metric === 'string') {
      if (key.toLowerCase().includes('time')) {
        try {
          return formatDistanceToNow(new Date(metric), { addSuffix: true });
        } catch {
          return metric;
        }
      }
      return metric;
    }
    return JSON.stringify(metric);
  };

  const getSentimentIcon = (sentiment: string | undefined) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return <SentimentVerySatisfiedIcon color="success" />;
      case 'negative':
        return <SentimentVeryDissatisfiedIcon color="error" />;
      default:
        return <SentimentNeutralIcon color="warning" />;
    }
  };

  // Add function to toggle filters visibility
  const toggleFilters = () => {
    setFiltersVisible(prev => !prev);
  };

  // Add eligible calls computation just before the return statement in the component
  const eligibleCalls = Array.isArray(calls) ? calls.filter((call) => !call.call_attempted) : [];
  const eligibleCallIds = eligibleCalls.map((call) => call.id!);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={4}>
        {/* Filters Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
          }}
        >
          {/* Filters Header with Toggle */}
          <Box 
            sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: filtersVisible ? `1px solid ${alpha('#000', 0.08)}` : 'none',
              cursor: 'pointer'
            }}
            onClick={toggleFilters}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Filters
            </Typography>
            <IconButton size="small">
              {filtersVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          {/* Collapsible Filters Content */}
          {filtersVisible && (
            <Box sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <AgentSelect
                      value={filters.agent_id ? parseInt(filters.agent_id.toString()) : null}
                      onChange={(value) => handleFilterChange('agent_id', value)}
                      label="Agent"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                      label="From Date"
                      value={filters.from_dt}
                      onChange={(date) => handleFilterChange('from_dt', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <DatePicker
                      label="To Date"
                      value={filters.to_dt}
                      onChange={(date) => handleFilterChange('to_dt', date)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: alpha('#000', 0.02),
                            },
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Campaign</InputLabel>
                      <Select
                        value={filters.campaign_id}
                        onChange={(e) => handleFilterChange('campaign_id', e.target.value)}
                        label="Campaign"
                      >
                        <MenuItem value="">
                          <em>All Campaigns</em>
                        </MenuItem>
                        {(campaigns || []).map((campaign) => (
                          <MenuItem key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        multiple
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        label="Status"
                        renderValue={(selected) => (
                          <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.5,
                            maxWidth: '100%',
                            overflow: 'hidden'
                          }}>
                            {((selected as string[]) || []).map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                onDelete={(e) => {
                                  e.stopPropagation();
                                  const newStatus = filters.status.filter(s => s !== value);
                                  handleFilterChange('status', newStatus);
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                sx={{
                                  height: 24,
                                  maxWidth: '100%',
                                  '& .MuiChip-label': {
                                    px: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            backgroundColor: alpha('#000', 0.02),
                          },
                        }}
                      >
                        <MenuItem value="pending">
                          <Chip label="Pending" size="small" color="primary" sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="placed">
                          <Chip label="Placed" size="small" color="success" sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="failed">
                          <Chip label="Failed" size="small" color="error" sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="reminder">
                          <Chip label="Reminder" size="small" color="info" icon={<AccessTimeIcon />} sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="flagged">
                          <Chip label="Flagged" size="small" color="error" sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="has_tasks">
                          <Chip label="Has Tasks" size="small" color="success" icon={<TaskAltIcon />} sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="has_messages">
                          <Chip label="Has Messages" size="small" color="info" icon={<SmsIcon />} sx={{ height: 24 }} />
                        </MenuItem>
                        <MenuItem value="has_actions">
                          <Chip label="Has Actions" size="small" color="warning" icon={<BuildIcon />} sx={{ height: 24 }} />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={resetFilters}
                        size="small"
                        sx={{
                          borderStyle: 'dashed',
                          color: 'text.secondary',
                          flex: 1,
                        }}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        onClick={applyFilters}
                        startIcon={<SearchIcon />}
                        size="small"
                        sx={{ flex: 1 }}
                      >
                        Search
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Calls Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Scheduled Calls
              </Typography>

              <Stack direction="row" spacing={1}>
                {selectedCalls.length > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={openBatchDeleteDialog}
                    sx={{
                      borderStyle: 'dashed',
                    }}
                  >
                    Delete Selected
                  </Button>
                )}
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={fetchCalls}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: alpha('#000', 0.04),
                      },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedCalls.length > 0 && selectedCalls.length < eligibleCallIds.length
                      }
                      checked={selectedCalls.length === eligibleCallIds.length && eligibleCallIds.length > 0}
                      onChange={(e) =>
                        e.target.checked
                          ? setSelectedCalls(eligibleCallIds)
                          : setSelectedCalls([])
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                        <TableSortLabel
                          active={sortConfig.field === 'schedule_dt'}
                          direction={sortConfig.order}
                          onClick={() =>
                            setSortConfig({
                              field: 'schedule_dt',
                              order:
                                sortConfig.field === 'schedule_dt' && sortConfig.order === 'asc'
                                  ? 'desc'
                                  : 'asc',
                            })
                          }
                        >
                          Schedule Time
                        </TableSortLabel>
                      </Box>
                      <Box sx={{ height: 32 }} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                        <TableSortLabel
                          active={sortConfig.field === 'from_number'}
                          direction={sortConfig.order}
                          onClick={() =>
                            setSortConfig({
                              field: 'from_number',
                              order:
                                sortConfig.field === 'from_number' && sortConfig.order === 'asc'
                                  ? 'desc'
                                  : 'asc',
                            })
                          }
                        >
                          From
                        </TableSortLabel>
                      </Box>
                      <PhoneNumberSelect
                        value={filters.from_number || ''}
                        onChange={(value) => handleFilterChange('from_number', value)}
                        label=""
                        showAllOption={true}
                        fullWidth={false}
                        sx={{
                          minWidth: 120,
                          '& .MuiOutlinedInput-root': {
                            height: 32,
                            backgroundColor: alpha('#000', 0.02),
                            '&:hover': {
                              backgroundColor: alpha('#000', 0.03),
                            },
                            '& .MuiSelect-select': {
                              paddingTop: 0,
                              paddingBottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                            }
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                        <TableSortLabel
                          active={sortConfig.field === 'to_number'}
                          direction={sortConfig.order}
                          onClick={() =>
                            setSortConfig({
                              field: 'to_number',
                              order:
                                sortConfig.field === 'to_number' && sortConfig.order === 'asc'
                                  ? 'desc'
                                  : 'asc',
                            })
                          }
                        >
                          To
                        </TableSortLabel>
                      </Box>
                      <TextField
                        size="small"
                        value={filters.to_number}
                        onChange={(e) => handleFilterChange('to_number', e.target.value)}
                        placeholder="Filter..."
                        sx={{
                          width: 120,
                          '& .MuiOutlinedInput-root': {
                            height: 32,
                            backgroundColor: alpha('#000', 0.02),
                            '&:hover': {
                              backgroundColor: alpha('#000', 0.03),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '4px 8px',
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                        <TableSortLabel
                          active={sortConfig.field === 'agent_id'}
                          direction={sortConfig.order}
                          onClick={() =>
                            setSortConfig({
                              field: 'agent_id',
                              order:
                                sortConfig.field === 'agent_id' && sortConfig.order === 'asc'
                                  ? 'desc'
                                  : 'asc',
                            })
                          }
                        >
                          Agent
                        </TableSortLabel>
                      </Box>
                      <Box sx={{ height: 32 }} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
                        <TableSortLabel
                          active={sortConfig.field === 'status'}
                          direction={sortConfig.order}
                          onClick={() =>
                            setSortConfig({
                              field: 'status',
                              order:
                                sortConfig.field === 'status' && sortConfig.order === 'asc'
                                  ? 'desc'
                                  : 'asc',
                            })
                          }
                        >
                          Status
                        </TableSortLabel>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          multiple
                          value={filters.status}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          renderValue={(selected) => (
                            <Box sx={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 0.5,
                              maxWidth: '100%',
                              overflow: 'hidden'
                            }}>
                              {((selected as string[]) || []).map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  onDelete={(e) => {
                                    e.stopPropagation();
                                    const newStatus = filters.status.filter(s => s !== value);
                                    handleFilterChange('status', newStatus);
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                  sx={{
                                    height: 24,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                      px: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                          displayEmpty
                          sx={{
                            height: 'auto',
                            minHeight: 32,
                            backgroundColor: alpha('#000', 0.02),
                            '&:hover': {
                              backgroundColor: alpha('#000', 0.03),
                            },
                            '& .MuiSelect-select': {
                              py: 0.5,
                              px: 1,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.5,
                              minHeight: '32px !important',
                              height: 'auto',
                            },
                          }}
                        >
                          <MenuItem value="pending">
                            <Chip label="Pending" size="small" color="primary" sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="placed">
                            <Chip label="Placed" size="small" color="success" sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="failed">
                            <Chip label="Failed" size="small" color="error" sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="reminder">
                            <Chip label="Reminder" size="small" color="info" icon={<AccessTimeIcon />} sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="flagged">
                            <Chip label="Flagged" size="small" color="error" sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="has_tasks">
                            <Chip label="Has Tasks" size="small" color="success" icon={<TaskAltIcon />} sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="has_messages">
                            <Chip label="Has Messages" size="small" color="info" icon={<SmsIcon />} sx={{ height: 24 }} />
                          </MenuItem>
                          <MenuItem value="has_actions">
                            <Chip label="Has Actions" size="small" color="warning" icon={<BuildIcon />} sx={{ height: 24 }} />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ height: 56, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Actions
                      </Box>
                      <Box sx={{ height: 32 }} />
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : calls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No scheduled calls found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getSortedCalls()
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((call) => (
                      <TableRow
                        key={call.id}
                        hover
                        selected={selectedCalls.includes(call.id!)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            disabled={call.call_attempted}
                            checked={selectedCalls.includes(call.id!)}
                            onChange={() => toggleCallSelection(call.id!)}
                          />
                        </TableCell>
                        <TableCell>
                          {call.schedule_dt
                            ? format(new Date(call.schedule_dt), 'MMM d, yyyy HH:mm')
                            : 'Immediate'}
                        </TableCell>
                        <TableCell>{call.from_number}</TableCell>
                        <TableCell>{call.to_number}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={<AgentLabel agentId={call.metrics?.synth_agent_id || getAgentId(call)} />}
                              sx={{
                                backgroundColor: alpha('#000', 0.04),
                                color: 'text.primary',
                                ...(call.metrics?.synth_agent_id && {
                                  backgroundColor: alpha('#2196f3', 0.1),
                                  color: 'primary.main',
                                })
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {/* Show reminder badge for all reminder calls */}
                            {(() => {
                              const config = typeof call.outbound_call_config === 'string' 
                                ? JSON.parse(call.outbound_call_config) 
                                : call.outbound_call_config;
                              return config.is_reminder && (
                                <Chip
                                  label="Reminder"
                                  color="info"
                                  size="small"
                                  icon={<AccessTimeIcon />}
                                />
                              );
                            })()}
                            
                            {/* Show status chip if no metrics or call failed */}
                            {(!call.metrics || (call.call_attempted && !call.call_placed)) && (
                              <Chip
                                label={call.call_placed ? 'Completed' : call.call_attempted ? 'Failed' : 'Scheduled'}
                                color={call.call_placed ? 'success' : call.call_attempted ? 'error' : 'default'}
                                size="small"
                              />
                            )}
                            
                            {/* Metrics Indicators */}
                            {call.metrics && (
                              <Stack direction="row" spacing={1} alignItems="center">
                                {/* Call Duration - Show directly instead of icon */}
                                {call.metrics.call_duration && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                    {call.metrics.call_duration}
                                  </Typography>
                                )}
                                
                                {/* Compact Stats Display */}
                                <Stack direction="row" spacing={0.5} alignItems="center" 
                                  sx={{ 
                                    bgcolor: alpha('#000', 0.04), 
                                    px: 1, 
                                    py: 0.5, 
                                    borderRadius: 1,
                                    typography: 'caption'
                                  }}>
                                  {/* Tasks */}
                                  {(call.metrics?.auto_tasks?.extracted_tasks?.length ?? 0) > 0 && (
                                    <Tooltip title={`${call.metrics?.auto_tasks?.extracted_tasks?.length} Task${
                                      (call.metrics?.auto_tasks?.extracted_tasks?.length ?? 0) > 1 ? 's' : ''
                                    } Created`}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TaskAltIcon sx={{ 
                                          fontSize: 16,
                                          color: call.metrics?.auto_tasks?.api_results?.some(r => r.success)
                                            ? 'success.main'
                                            : 'error.main'
                                        }} />
                                        {call.metrics?.auto_tasks?.extracted_tasks?.length}
                                      </Box>
                                    </Tooltip>
                                  )}
                                  
                                  {/* Text Messages */}
                                  {(call.metrics?.auto_text_messages?.extracted_messages?.length ?? 0) > 0 && (
                                    <Tooltip title={`${call.metrics?.auto_text_messages?.extracted_messages?.length} Message${
                                      (call.metrics?.auto_text_messages?.extracted_messages?.length ?? 0) > 1 ? 's' : ''
                                    } Sent`}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <SmsIcon sx={{ 
                                          fontSize: 16,
                                          color: call.metrics?.auto_text_messages?.api_results?.some(r => 
                                            r.success === true || (r.success && typeof r.success === 'object' && r.success.success === true)
                                          )
                                            ? 'success.main'
                                            : 'error.main'
                                        }} />
                                        {call.metrics?.auto_text_messages?.extracted_messages?.length}
                                      </Box>
                                    </Tooltip>
                                  )}

                                  {/* Other Actions */}
                                  {(call.metrics?.tools_used?.length ?? 0) > 0 && (
                                    <Tooltip title={`${call.metrics?.tools_used?.length} Other Action${
                                      (call.metrics?.tools_used?.length ?? 0) > 1 ? 's' : ''
                                    } Taken`}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BuildIcon sx={{ 
                                          fontSize: 16,
                                          color: call.metrics?.tools_used?.some((t: ToolUsage) => t.tool_result.toLowerCase().includes('success'))
                                            ? 'success.main'
                                            : 'error.main'
                                        }} />
                                        {call.metrics?.tools_used?.length}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                                
                                {/* Horrible Call Indicator */}
                                {call.metrics.horrible_call && (
                                  <Tooltip title="This call was flagged for review">
                                    <Chip
                                      size="small"
                                      label="Flagged"
                                      color="error"
                                      sx={{ height: 20 }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {hasMetrics(call) && (
                              <Tooltip title="View Metrics">
                                <IconButton
                                  size="small"
                                  onClick={() => openMetricsDialog(call)}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      backgroundColor: alpha('#000', 0.04),
                                    },
                                  }}
                                >
                                  <InsightsIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={call.call_attempted ? "Cannot delete attempted calls" : "Delete"}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openDeleteDialog(call.id!)}
                                  disabled={call.call_attempted}
                                  sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                      color: 'error.main',
                                      backgroundColor: alpha('#000', 0.04),
                                    },
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={calls.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          />
        </Paper>
      </Stack>

      {/* Metrics Dialog */}
      <Dialog
        open={metricsDialogOpen}
        onClose={closeMetricsDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
            height: '90vh',  // Set a fixed height relative to viewport
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AnalyticsIcon />
            <Typography variant="h6">Call Analytics</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedCallMetrics && (
            <Grid container spacing={3}>
              {/* Call Overview */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#000', 0.02),
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <CallEndIcon />
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="subtitle1">Call Summary</Typography>
                      <Stack direction="row" spacing={3} mt={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Duration</Typography>
                          <Typography variant="body2">
                            {selectedCallMetrics.call_duration || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Call Time</Typography>
                          <Typography variant="body2">
                            {selectedCallMetrics.call_time ? 
                              format(new Date(selectedCallMetrics.call_time), 'PPp') : 
                              'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Typography variant="body2">
                            {selectedCallMetrics.call_termination || 'N/A'}
                          </Typography>
                        </Box>
                        {selectedCallMetrics.synth_agent_id && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">Agent</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                              <AgentLabel agentId={selectedCallMetrics.synth_agent_id} />
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              {/* Left Column - Satisfaction Analysis and Sentiment */}
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  {/* Sentiment Analysis */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1">
                        Sentiment Analysis
                      </Typography>
                      {renderScore(selectedCallMetrics.caller_satisfaction)}
                    </Box>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1">
                            {selectedCallMetrics.sentiment_analysis || 'No sentiment data'}
                          </Typography>
                        </Box>
                        {selectedCallMetrics.caller_satisfaction_reasoning && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                            {selectedCallMetrics.caller_satisfaction_reasoning}
                          </Typography>
                        )}
                      </Box>

                      {selectedCallMetrics.horrible_call && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          This call was flagged for review
                        </Typography>
                      )}
                    </Stack>
                  </Paper>

                  {/* Call Flow Analysis - Only show if script_steps exists */}
                  {selectedCallMetrics.script_steps && Object.keys(selectedCallMetrics.script_steps).length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        Call Flow Analysis
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries((selectedCallMetrics.script_steps as Record<string, boolean>) || {}).map(([stepKey, stepValue]) => {
                          const readableKey = stepKey
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');

                          return (
                            <Grid item xs={12} key={stepKey}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: stepValue ? alpha('#000', 0.04) : 'transparent',
                                  border: '1px solid',
                                  borderColor: stepValue ? alpha('#000', 0.2) : alpha('#000', 0.08),
                                }}
                              >
                                {stepValue ? (
                                  <CheckCircleIcon sx={{ fontSize: 16, color: 'text.primary' }} />
                                ) : (
                                  <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                )}
                                <Typography variant="body2" color={stepValue ? 'text.primary' : 'text.secondary'}>
                                  {readableKey}
                                </Typography>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Paper>
                  )}
                </Stack>
              </Grid>

              {/* Right Column - Automation and Transcript */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3} sx={{ height: '100%' }}>
                  {/* Automation Results Section */}
                  <Grid container spacing={2}>
                    {/* Auto Tasks Section */}
                    <Grid item xs={12} md={selectedCallMetrics.auto_text_messages?.extracted_messages?.length > 0 ? 6 : 12}>
                      {selectedCallMetrics.auto_tasks?.extracted_tasks?.length > 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TaskAltIcon />
                            Automated Tasks
                          </Typography>
                          <Stack spacing={2} sx={{ maxHeight: '250px', overflow: 'auto' }}>
                            {(selectedCallMetrics.auto_tasks?.extracted_tasks || []).map((task: { title: string; description: string; assignees: (string | null)[] }, idx: number) => (
                              <Paper
                                key={`task-${idx}`}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: alpha('#000', 0.02),
                                  border: '1px solid',
                                  borderColor: selectedCallMetrics.auto_tasks?.api_results[idx]?.success 
                                    ? 'success.main' 
                                    : 'error.main',
                                  borderStyle: 'dashed',
                                }}
                              >
                                <Stack spacing={1}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">{task.title}</Typography>
                                    {selectedCallMetrics.auto_tasks?.api_results[idx]?.success ? (
                                      <Chip label="Created" size="small" color="success" />
                                    ) : (
                                      <Chip label="Failed" size="small" color="error" />
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {task.description}
                                  </Typography>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        </Paper>
                      )}
                    </Grid>

                    {/* Auto Text Messages Section */}
                    <Grid item xs={12} md={selectedCallMetrics.auto_tasks?.extracted_tasks?.length > 0 ? 6 : 12}>
                      {selectedCallMetrics.auto_text_messages?.extracted_messages?.length > 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            height: '100%',
                          }}
                        >
                          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmsIcon />
                            Automated Text Messages
                          </Typography>
                          <Stack spacing={2} sx={{ maxHeight: '250px', overflow: 'auto' }}>
                            {(selectedCallMetrics.auto_text_messages?.extracted_messages || []).map((message: { content: string; reason: string; type: string }, idx: number) => {
                              const apiResult = selectedCallMetrics.auto_text_messages?.api_results[idx];
                              const isSuccess = apiResult?.success === true || (typeof apiResult?.success === 'object' && apiResult?.success?.success === true);
                              const errorMessage = typeof apiResult?.success === 'object' ? apiResult?.success?.message : undefined;
                              
                              return (
                                <Paper
                                  key={`message-${idx}`}
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: alpha('#000', 0.02),
                                    border: '1px solid',
                                    borderColor: isSuccess ? 'success.main' : 'error.main',
                                    borderStyle: 'dashed',
                                  }}
                                >
                                  <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SmsIcon fontSize="small" />
                                        {message.type === 'snippet' ? 'Snippet' : 'Text Message'}
                                      </Typography>
                                      {isSuccess ? (
                                        <Chip label="Sent" size="small" color="success" />
                                      ) : (
                                        <Chip label="Failed" size="small" color="error" />
                                      )}
                                    </Box>
                                    <Typography variant="body1">
                                      {message.content}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Reason: {message.reason}
                                    </Typography>
                                    {errorMessage && (
                                      <Typography variant="body2" color="error.main">
                                        Error: {errorMessage}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Stack>
                        </Paper>
                      )}
                    </Grid>
                  </Grid>

                  {/* Tools Used Section */}
                  {selectedCallMetrics.tools_used?.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BuildIcon />
                        Actions Taken
                      </Typography>
                      <Stack spacing={2} sx={{ maxHeight: '200px', overflow: 'auto' }}>
                        {(selectedCallMetrics.tools_used || []).map((tool: ToolUsage, idx: number) => (
                          <Paper
                            key={`tool-${idx}`}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: alpha('#000', 0.02),
                              border: '1px solid',
                              borderColor: tool.tool_result.toLowerCase().includes('success') ? 'success.main' : 'error.main',
                              borderStyle: 'dashed',
                            }}
                          >
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {tool.tool_name}
                                  {tool.tool_result.toLowerCase().includes('success') ? (
                                    <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                                  ) : (
                                    <ErrorIcon color="error" sx={{ fontSize: 16 }} />
                                  )}
                                </Typography>
                                <Tooltip title={`Execution time: ${tool.time_to_perform.toFixed(2)}s`}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {tool.tool_call_time} - {tool.tool_return_time}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Result: {tool.tool_result}
                              </Typography>
                              {Object.keys(tool.tool_input).length > 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ 
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '0.8rem'
                                }}>
                                  Input: {JSON.stringify(tool.tool_input, null, 2)}
                                </Typography>
                              )}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  )}

                  {/* Transcript */}
                  {selectedCallMetrics.transcript && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 400,
                        maxHeight: '100%',
                        height: 'auto',
                        flex: '1 1 0',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography variant="subtitle1" gutterBottom>
                        Conversation Transcript
                      </Typography>
                      <Box sx={{ 
                        flex: 1,
                        overflow: 'auto',
                        minHeight: 0,
                      }}>
                        <List sx={{ 
                          width: '100%',
                          bgcolor: 'background.paper',
                        }}>
                          {(selectedCallMetrics.transcript || []).map((message, index) => (
                            <ListItem key={index} alignItems="flex-start" sx={{
                              borderLeft: message.role === 'assistant' ? '4px solid #3f51b5' : '4px solid #f50057',
                              mb: 1,
                              backgroundColor: message.role === 'assistant' ? 'rgba(63, 81, 181, 0.08)' : 'rgba(245, 0, 87, 0.08)'
                            }}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="subtitle2"
                                    color={message.role === 'assistant' ? 'primary' : 'text.primary'}
                                  >
                                    {message.role === 'assistant' ? 'AI Assistant' : 'Customer'}
                                  </Typography>
                                }
                                secondary={message.content}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Paper>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeMetricsDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
          },
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this scheduled call?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Delete Confirmation Dialog */}
      <Dialog
        open={batchDeleteDialogOpen}
        onClose={closeBatchDeleteDialog}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#000', 0.08),
          },
        }}
      >
        <DialogTitle>Confirm Batch Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedCalls.length} scheduled calls?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeBatchDeleteDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={confirmBatchDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScheduledCallsList; 